import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAiCostsCurrent } from "@/hooks/use-ai-costs";
import { usePermissions } from "@/hooks/use-permissions";
import { useBrandContext } from "@/contexts/brand-context";
import { useLocale, formatNumber } from "@/i18n/format";

function barColor(pct: number) {
  if (pct >= 1) return "bg-red-500";
  if (pct >= 0.8) return "bg-orange-500";
  if (pct >= 0.5) return "bg-yellow-500";
  return "bg-emerald-500";
}

export function AiSpendWidget() {
  const { t } = useTranslation("dashboard");
  const locale = useLocale();
  const { brand } = useBrandContext();
  const { canViewAiCosts } = usePermissions();
  const { data, isLoading } = useAiCostsCurrent(brand?.id ?? "");

  if (!canViewAiCosts || !brand) return null;
  if (isLoading || !data) {
    return <div className="h-36 animate-pulse rounded-lg border bg-muted" />;
  }

  const spent = Number(data.spent);
  const cap = data.cap ? Number(data.cap) : null;
  const pct = data.percentUsed;
  const top3 = [...data.breakdownByModel]
    .sort((a, b) => Number(b.cost) - Number(a.cost))
    .slice(0, 3);

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">{t("aiSpend.title")}</h3>
        <Link to={`/${brand.slug}/settings/ai-usage`} className="text-xs text-muted-foreground hover:text-foreground">
          {t("aiSpend.viewDetails")}
        </Link>
      </div>
      <div className="mt-2 text-2xl font-semibold">
        {formatNumber(spent, locale, { style: "currency", currency: "USD" })}
        {cap !== null
          ? <span className="text-muted-foreground text-sm"> / {formatNumber(cap, locale, { style: "currency", currency: "USD" })}</span>
          : <span className="text-muted-foreground text-sm"> · {t("aiSpend.noCap")}</span>}
      </div>
      {cap !== null && (
        <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-muted">
          <div className={`h-full ${barColor(pct)}`} style={{ width: `${Math.min(100, pct * 100)}%` }} />
        </div>
      )}
      <ul className="mt-3 space-y-1 text-xs">
        {top3.map((m) => (
          <li key={m.model} className="flex justify-between">
            <span>{m.model}</span>
            <span>${Number(m.cost).toFixed(2)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
